/**
 * شاشة «سجل المعلمين» — `/teachers` (design/TeacherFile.dc.html).
 * «سجل المعلمين له نفس الشكل بالظبط — قائمة ثم سجل فرد» (ملاحظة التصميم).
 *
 * و`/teachers/{id}` بيطابق نفس المسار في `ROUTES`، فالشاشة بتحوّل لـ
 * `TeacherFile` بنفسها — زي `Students` بالحرف.
 *
 * ⚠️ **عدد الطلاب**: بيرجع من السيستم محسوبًا من `enrollments` (طلاب
 *    مميّزين مش اشتراكات). و`null` معناها **محجوب عن قسمك** مش صفر —
 *    والشاشة بتكتب «محجوب» بدل ما تعرض 0 وتكدب.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Shell } from "@/components/Shell";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  NoResultsState,
  OfflineState,
} from "@/components/states/ScreenStates";
import { Avatar, Dash, NeedsReviewMark } from "@/components/RegistryBits";
import { Ltr, fmtNum } from "@/lib/format";
import { errorMessageAr, OfflineError } from "@/api/client";
import { useRouter } from "@/lib/router";
import {
  TEACHER_SEGMENTS,
  fetchTeacherCounts,
  fetchTeachers,
  latinDigits,
  teacherTone,
  type Page,
  type TeacherRow,
  type TeacherSegment,
} from "@/api/registry";
import { TeacherFile } from "@/screens/TeacherFile";
import "@/styles/registry.css";

// ٨ كانت بتعمل ٧ صفحات لـ٥٦ معلم — بلاغ الإشراف الفني: «عدد المعلمين المعروض قليل»
const PER_PAGE = 25;

/** مكان الموظف في القائمة (الشريحة · الصفحة · البحث) — بيرجع له بعد فتح ملف
 *  وتعديله. بلاغ الإشراف الفني: «لما بعمل تعديل بيرجعني لبداية السجل». */
const LIST_STATE_KEY = "rattel.teachers.list";
type ListMemory = { segment: TeacherSegment; page: number; search: string };
function readListMemory(): ListMemory | null {
  try {
    const raw = window.sessionStorage.getItem(LIST_STATE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<ListMemory>;
    return {
      segment: (v.segment as TeacherSegment) || "all",
      page: Math.max(1, Number(v.page) || 1),
      search: typeof v.search === "string" ? v.search : "",
    };
  } catch {
    return null;
  }
}
const MIN_SEARCH = 2;
const SEARCH_DEBOUNCE_MS = 350;

type PageState =
  | { kind: "loading" }
  | { kind: "ready"; value: Page<TeacherRow> }
  | { kind: "error"; messageAr: string }
  | { kind: "offline" };

/** `/teachers/9f2…` ⇒ `9f2…` · `/teachers` ⇒ `null` */
export function teacherIdFromPath(path: string): string | null {
  const m = /^\/teachers\/([^/?#]+)/.exec(path);
  return m && m[1] ? decodeURIComponent(m[1]) : null;
}

export function Teachers() {
  const { path } = useRouter();
  const teacherId = teacherIdFromPath(path);
  if (teacherId) return <TeacherFile teacherId={teacherId} />;
  return <TeachersList />;
}

function TeachersList() {
  const { navigate } = useRouter();

  const remembered = useRef(readListMemory()).current;
  const [segment, setSegment] = useState<TeacherSegment>(remembered?.segment ?? "all");
  const [page, setPage] = useState(remembered?.page ?? 1);
  const [searchInput, setSearchInput] = useState(remembered?.search ?? "");
  const [query, setQuery] = useState(
    (remembered?.search ?? "").trim().length >= MIN_SEARCH ? (remembered?.search ?? "").trim() : "",
  );

  useEffect(() => {
    try {
      window.sessionStorage.setItem(
        LIST_STATE_KEY,
        JSON.stringify({ segment, page, search: searchInput } satisfies ListMemory),
      );
    } catch {
      /* التخزين مش متاح — القائمة بتشتغل عادي من غير ذاكرة */
    }
  }, [segment, page, searchInput]);
  const [state, setState] = useState<PageState>({ kind: "loading" });
  const [counts, setCounts] = useState<Record<TeacherSegment, number | null> | null>(null);

  const debounce = useRef<number | null>(null);
  useEffect(() => {
    const trimmed = searchInput.trim();
    const next = trimmed.length >= MIN_SEARCH ? trimmed : "";
    if (debounce.current) window.clearTimeout(debounce.current);
    debounce.current = window.setTimeout(() => {
      // ⚠️ الصفحة ما تترجعش للأولى غير لو البحث اتغيّر فعلًا — وإلا الذاكرة
      //    المسترجعة كانت بتتداس أول ما الشاشة تفتح.
      setQuery((cur) => {
        if (cur !== next) setPage(1);
        return cur === next ? cur : next;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounce.current) window.clearTimeout(debounce.current);
    };
  }, [searchInput]);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const value = await fetchTeachers({ segment, page, perPage: PER_PAGE, q: query || null });
      setState({ kind: "ready", value });
    } catch (err) {
      console.error("[teachers] تحميل سجل المعلمين فشل", err);
      setState(
        err instanceof OfflineError
          ? { kind: "offline" }
          : { kind: "error", messageAr: errorMessageAr(err) },
      );
    }
  }, [segment, page, query]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let alive = true;
    setCounts(null);
    void fetchTeacherCounts({ q: query || null }).then((c) => {
      if (alive) setCounts(c);
    });
    return () => {
      alive = false;
    };
  }, [query]);

  const data = state.kind === "ready" ? state.value : null;
  const rows = data?.rows ?? [];
  const active = TEACHER_SEGMENTS.find((s) => s.key === segment) ?? TEACHER_SEGMENTS[0];
  const total = data?.total ?? 0;
  const shownPage = data?.page ?? page;
  const shownPerPage = data?.perPage ?? PER_PAGE;
  const firstIndex = total === 0 ? 0 : (shownPage - 1) * shownPerPage + 1;
  const lastIndex = total === 0 ? 0 : firstIndex + rows.length - 1;
  const totalPages = data?.totalPages ?? 1;
  // ⚠️ لو العدّ محجوب، العمود بيقول «محجوب» — مش صفر
  const countsVisible = data?.countsVisible ?? true;

  const crumb =
    counts?.all !== null && counts?.all !== undefined ? (
      <>
        <span className="num">{fmtNum(counts.all)}</span> معلم في السجل
      </>
    ) : null;

  return (
    <Shell
      title="سجل المعلمين"
      {...(crumb ? { crumb } : {})}
      search={{
        value: searchInput,
        onChange: setSearchInput,
        placeholder: "ابحث باسم المعلم أو كوده…",
      }}
    >
      {/* ═══ (1) الشرايح ═══ */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {TEACHER_SEGMENTS.map((s) => {
          const n = counts?.[s.key] ?? null;
          return (
            <button
              key={s.key}
              type="button"
              className={`filt${segment === s.key ? " on" : ""}`}
              aria-pressed={segment === s.key}
              onClick={() => {
                setSegment(s.key);
                setPage(1);
              }}
            >
              {s.chip}
              {n === null ? null : (
                <>
                  {" "}
                  <span className="num">{fmtNum(n)}</span>
                </>
              )}
            </button>
          );
        })}
      </div>

      {/* ═══ (2) الجدول ═══ */}
      <div className="card" style={{ overflow: "hidden" }}>
        <div className="sec-h" style={{ padding: "14px 16px 10px" }}>
          <span className="sec-title">{active?.title}</span>
          <span className="t-meta">
            مرتّب بالاسم
            {query ? ` · بحث عن «${query}»` : ""}
            {countsVisible ? "" : " · عدد الطلاب محجوب عن قسمك"}
          </span>
        </div>

        {state.kind === "loading" ? <LoadingState rows={5} /> : null}
        {state.kind === "offline" ? <OfflineState onRetry={() => void load()} /> : null}
        {state.kind === "error" ? (
          <ErrorState messageAr={state.messageAr} onRetry={() => void load()} />
        ) : null}

        {state.kind === "ready" && rows.length === 0 && query ? (
          <NoResultsState query={query} onClear={() => setSearchInput("")} />
        ) : null}
        {state.kind === "ready" && rows.length === 0 && !query && segment !== "all" ? (
          <EmptyState
            title={`مفيش معلمين في «${active?.chip}»`}
            hint="جرّب شريحة تانية أو شوف «الكل»"
            action={{ label: "عرض الكل", onClick: () => setSegment("all") }}
          />
        ) : null}
        {state.kind === "ready" && rows.length === 0 && !query && segment === "all" ? (
          <EmptyState
            title="لسه مفيش معلمين في السجل"
            hint="المعلم بيدخل السجل بعد ما يعدّي رحلة التوظيف"
          />
        ) : null}

        {rows.length > 0 ? (
          <div className="reg-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th>المعلم</th>
                  <th>المواد</th>
                  <th>اللغات</th>
                  <th>عدد الطلاب</th>
                  <th>المشرفة</th>
                  <th>الحالة</th>
                  <th>إجراء</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="row-link">
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <Avatar name={row.name_ar} size={28} />
                        <div style={{ minWidth: 0 }}>
                          {/* 🔑 **الاسم هو نفسه الرابط.**
                              المقاس على 390: الجدول عرضه 684 في حاوية 282،
                              وزرار «فتح الملف» في آخر عمود `left = -366` —
                              يعني الفعل الأساسي على بُعد ٢.٤ عرض شاشة سحب
                              أفقي. والصف **مش** بديل: `.row-link` مجرد لون
                              تحويم، ومفيش `<tr>` واحد في السيستم عنده
                              `onClick`.
                              ⛔ وممنوع `onClick` على الـ`<tr>` نفسه — الصف
                              فيه أزرار جوّاه وده بيعمل نقر مزدوج. */}
                          <button
                            type="button"
                            className="link t-body clip"
                            style={{ fontWeight: 600, color: "var(--ink)", textAlign: "start", maxWidth: "100%" }}
                            onClick={() => navigate(`/teachers/${row.id}`)}
                          >
                            {row.name_ar || <span className="missing">بلا اسم مسجّل</span>}
                          </button>
                          <div
                            className="t-meta"
                            style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}
                          >
                            {row.teacher_code ? <Ltr>{row.teacher_code}</Ltr> : null}
                            <NeedsReviewMark q={row.data_quality} />
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="t-desc clip">
                      {row.subjects.length > 0 ? row.subjects.join(" · ") : <Dash />}
                    </td>
                    <td className="t-desc">
                      {row.languages_ar.length > 0 ? row.languages_ar.join(" · ") : <Dash />}
                    </td>
                    <td className="t-desc">
                      {/* ⛔ `null` ≠ 0: المحجوب بيتقال، ما بيتعرضش صفر */}
                      {row.students_count === null ? (
                        <span className="missing">محجوب</span>
                      ) : (
                        <span className="num">{fmtNum(row.students_count)}</span>
                      )}
                    </td>
                    <td className="t-desc">
                      {/* المشرفة المربوطة بحساب أولًا، وإلا الاسم الخام من
                          قائمة المشرفات — من غيره 18 معلمًا مشرفاتهم معروفة
                          بيبانوا «بلا مشرفة». */}
                      {row.supervisor
                        ? row.supervisor.name_ar
                        : row.supervisor_name_raw
                          ? <span className="pending-link">{row.supervisor_name_raw}</span>
                          : <Dash />}
                    </td>
                    <td>
                      {row.status_label_ar ? (
                        <span className={`chip ${teacherTone(row.status)}`}>
                          <span className="bullet" />
                          {latinDigits(row.status_label_ar)}
                        </span>
                      ) : (
                        <Dash />
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn sm ghost"
                        onClick={() => navigate(`/teachers/${row.id}`)}
                      >
                        فتح الملف
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {/* ═══ (3) الترقيم ═══ */}
      {rows.length > 0 ? (
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span className="t-meta">
            عرض <span className="num">{fmtNum(firstIndex)}</span>–
            <span className="num">{fmtNum(lastIndex)}</span> من{" "}
            <span className="num">{fmtNum(total)}</span>
          </span>
          <button
            type="button"
            className="btn sm ghost"
            disabled={shownPage <= 1 || state.kind === "loading"}
            onClick={() => setPage(Math.max(1, shownPage - 1))}
          >
            السابق
          </button>
          <button
            type="button"
            className="btn sm ghost"
            disabled={shownPage >= totalPages || state.kind === "loading"}
            onClick={() => setPage(Math.min(totalPages, shownPage + 1))}
          >
            التالي
          </button>
          <span className="t-meta" style={{ marginRight: "auto" }}>
            عدد الطلاب محسوب من الاشتراكات النشطة — طلاب مميّزين مش اشتراكات.
          </span>
        </div>
      ) : null}
    </Shell>
  );
}
